import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
// import { Carousel } from "react-responsive-carousel";
// import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Suspense, useEffect, useState } from "react";
import { hotlist } from "../constants/hotlist";
import { specialPriceList } from "../constants/specialPriceList";
import { generateImageURL } from "../utils/convertCyrToLat";
import HeroCarousel from "../components/HeroCarousel";
import { modelNameMap } from "../utils/modelNameMap";

const container = {
  hidden: { opacity: 1, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      delayChildren: 0,
    },
  },
};

const item = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
  },
};

// Специални ID-та на автомобилите
const specialIds = new Set([
  "VEH172205",
  "VEH172207",
  "VEH173195",
  "VEH173813",
  "VEH173816",
  "VEH173770",
  "VEH173771",
  "VEH173772",
  "VEH173773",
  "VEH172211",
]);

const specialPriceIds = new Set(specialPriceList);

// Функция за обработка на грешки при зареждане на изображения
const onImageError = (e) => {
  e.target.src = "../images/default-car.jpg";
};

// Основен компонент
export default function CarList({ cars }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNoCarsMessage, setShowNoCarsMessage] = useState(false);

  // Получаване на параметри от URL
  const getParams = (param) =>
    searchParams.getAll(param).map((p) => p.toUpperCase()) || [];

  const modelsList = getParams("model");
  const fuelsList = getParams("fuel");
  const gearboxesList = getParams("gearbox");
  const vatsList = getParams("vat");

  // Филтриране на автомобили
  const filteredCars = cars.filter(
    (car) =>
      (modelsList.length === 0 ||
        modelsList.includes(
          modelNameMap[car.model.toUpperCase()] || car.model.toUpperCase(),
        )) &&
      (fuelsList.length === 0 || fuelsList.includes(car.fuel.toUpperCase())) &&
      (gearboxesList.length === 0 ||
        gearboxesList.includes(car.gearbox.toUpperCase())) &&
      (vatsList.length === 0 || vatsList.includes(car.vat.toUpperCase())),
  );

  //   const sortedCars = [...filteredCars].sort(
  //     (a, b) => a.new_price - b.new_price
  //   );

  // Сортиране по отстъпка
  const sortedCars = [...filteredCars].sort((a, b) => {
    const discountA = ((a.old_price - a.new_price) / a.old_price) * 100;
    const discountB = ((b.old_price - b.new_price) / b.old_price) * 100;

    // Ако и двата автомобила са в hotlist, сортирай по отстъпка
    if (hotlist.includes(a.id) && hotlist.includes(b.id)) {
      return discountB - discountA;
    }

    // Ако само единият е в hotlist, той трябва да е по-напред
    if (hotlist.includes(a.id)) return -1;
    if (hotlist.includes(b.id)) return 1;

    // Ако нито един не е в hotlist, сортирай по отстъпка
    return discountB - discountA;
  });

  // Генериране на налични модели
  // const availableModels = [...new Set(cars.map((car) => car.model))];

  // Изчистване на URL параметрите след 5 секунди, ако няма налични автомобили
  useEffect(() => {
    if (filteredCars.length === 0) {
      setShowNoCarsMessage(true);

      const timer = setTimeout(() => {
        setSearchParams({});
      }, 5000); // Изчистване след 5 секунди

      return () => clearTimeout(timer); // Почистете таймера при размонтиране
    } else {
      setShowNoCarsMessage(false);
    }
  }, [filteredCars.length, setSearchParams]);

  function capitalize(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  return (
    <>
      <HeroCarousel />
      <motion.div
        layout
        className="container grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
        variants={container}
        initial="hidden"
        animate="visible"
      >
        {sortedCars.length > 0 ? (
          sortedCars.map((car) => {
            const displayModel =
              modelNameMap[car.model.toUpperCase()] || car.model;
            const imageURL = specialIds.has(car.id)
              ? generateImageURL({ ...car, model: displayModel }).replace(
                  ".jpeg",
                  "-copy.jpeg",
                )
              : generateImageURL({ ...car, model: displayModel });

            const discount =
              ((car.old_price - car.new_price) / car.old_price) * 100;

            return (
              <motion.div
                layout
                className="relative p-2 mt-4 shadow-md item hover:shadow-lg"
                variants={item}
                key={car.id}
                id="the-list"
              >
                {specialPriceIds.has(car.id) && (
                  <div className="absolute top-0 right-0 w-[110px] h-[110px] overflow-hidden">
                    <div className="absolute top-[28px] right-[-40px] w-[160px] rotate-45 bg-yellow-400 text-fordBlue text-sm font-semibold text-center shadow-md py-2">
                      Специална цена
                    </div>
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className="min-h-[92px]">
                    <h3 className="mt-2 text-2xl uppercase text-fordBlue">
                      {modelNameMap[car.model] || car.model}
                    </h3>
                    <p className="text-sm text-gray-500">Оферта {car.id}</p>
                  </div>
                  {discount !== 0 && !specialPriceIds.has(car.id) && (
                    <div
                      className={`flex flex-col px-4 py-2 text-center rounded-sm min-w-[180px] ${
                        hotlist.includes(car.id) ? "bg-fordRed" : "bg-fordLight"
                      }`}
                    >
                      <p className="text-sm tracking-widest text-white uppercase">
                        {hotlist.includes(car.id)
                          ? "Ексклузивна оферта"
                          : "Специална оферта"}
                      </p>
                      <p className="text-sm tracking-widest text-center text-white uppercase ">
                        -
                        {new Intl.NumberFormat("bg", {
                          maximumFractionDigits: 0,
                        }).format(discount)}
                        %
                      </p>
                    </div>
                  )}
                </div>
                <Suspense fallback={<div>Зареждане...</div>}>
                  <img src={imageURL} alt={car.model} onError={onImageError} />
                </Suspense>
                <div className="flex justify-between">
                  <p className="text-gray-500">Гориво</p>
                  <p className="text-fordBlue">{car.fuel}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-gray-500">Скоростна кутия</p>
                  <p className="text-fordBlue">{car.gearbox}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-gray-500">Ниво на оборудване</p>
                  <p className="text-fordBlue">{car.trim}</p>
                </div>
                <p className="p-4 mt-4 text-xl text-center bg-gray-50">
                  Цена:{" "}
                  {new Intl.NumberFormat("bg", {
                    maximumFractionDigits: 0,
                  }).format(car.new_price)}{" "}
                  <span className="text-sm">€</span>
                </p>
                <div className="mx-auto text-center">
                  <Link to={car.id}>
                    <button className="mt-4 mb-2 border btn-primary border-fordBlue hover:bg-white hover:text-fordBlue">
                      Виж още
                    </button>
                  </Link>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="py-8 mt-16 text-center border rounded-lg text-fordBlue col-span-full">
            <h2 className="text-2xl">
              В момента няма налични автомобили {modelsList}
            </h2>
          </div>
        )}
      </motion.div>
    </>
  );
}
